import type { ProjectSummary, RouteState } from "../types";
import { resolvePhotoUrl } from "../photo-storage";
import { escapeHtml, renderPagePath } from "../view-helpers";

type DeclarationSectionSummary = {
  label: string;
  stateLabel: string;
  warning?: boolean;
  detail: string;
};

function buildDeclarationSummaries(project: ProjectSummary): DeclarationSectionSummary[] {
  const mode = project.declaration.entryMode;
  const pdfReady = Boolean(project.declaration.pdfFileName);
  const photoCount = project.declaration.photoDataUrls.length;
  const photoReady = photoCount > 0;
  const skipped = mode === "skip";
  const recognition = project.declaration.recognition;
  const recognitionStatusLabel =
    recognition.status === "recognized"
      ? "已有辅助结果"
      : recognition.status === "running"
        ? "处理中"
        : recognition.status === "failed"
          ? "上次失败"
          : "未生成";

  return [
    {
      label: "附件留存",
      stateLabel: pdfReady || photoReady || skipped
        ? pdfReady
          ? "已留存 PDF"
          : photoReady
            ? `已留存 ${photoCount} 张照片`
            : "稍后补传"
        : "待处理",
      warning: !(pdfReady || photoReady || skipped),
      detail:
        pdfReady || photoReady || skipped
          ? "当前验收申请表附件状态已经明确，后续仍可回来补传或替换。"
          : "当前还没有留存验收申请表附件；如果手头有材料，可以先在这里留存。",
    },
    {
      label: "辅助预填",
      stateLabel: recognitionStatusLabel,
      warning: recognition.status === "failed",
      detail:
        recognition.status === "recognized"
          ? "辅助预填结果已经生成，但当前项目仍以手动填写和人工核对为准。"
          : recognition.status === "running"
            ? "辅助预填正在处理中；这不会阻止你继续回到基本信息页手动填写。"
            : recognition.status === "failed"
              ? "上次辅助预填没有成功；当前项目仍按手动主线推进。"
              : "当前还没有辅助预填结果；这不会影响你继续按手动主线录入。",
    },
    {
      label: "主线衔接",
      stateLabel: "返回基本信息",
      detail: "当前页面只负责附件留存和辅助预填，真正的主线填写仍在“项目基本信息”页里继续。",
    },
  ];
}

function renderModeButton(
  label: string,
  inputId: string,
  disabled: boolean,
  selected: boolean,
  accept: string,
  multiple = false,
): string {
  return `
    <label class="upload-chip ${disabled ? "is-disabled" : ""} ${selected ? "is-selected" : ""}">
      <span>${label}</span>
      <input id="${inputId}" type="file" accept="${accept}" ${multiple ? "multiple" : ""} ${disabled ? "disabled" : ""} />
    </label>
  `;
}

function renderPdfPreview(project: ProjectSummary): string {
  if (!project.declaration.pdfFileName) {
    return "";
  }

  const previewUrl = resolvePhotoUrl(project.declaration.pdfFileDataUrl);

  return `
    <div class="panel stack">
      <div class="inline-head">
        <div>
          <p class="eyebrow">当前文件</p>
          <strong>${escapeHtml(project.declaration.pdfFileName)}</strong>
        </div>
        <div class="inline-actions">
          <a class="ghost-button small-button button-link" href="${previewUrl}" target="_blank" rel="noreferrer">预览</a>
          <button type="button" class="ghost-button small-button" data-action="clear-declaration-pdf">删除</button>
        </div>
      </div>
      <p class="mini-meta">${escapeHtml(project.declaration.sourceSummary || "已保存验收申请表 PDF。")}</p>
    </div>
  `;
}

function renderPhotoPreview(project: ProjectSummary): string {
  if (!project.declaration.photoDataUrls.length) {
    return "";
  }

  return `
    <div class="panel stack">
      <div class="inline-head">
        <div>
          <p class="eyebrow">当前照片</p>
          <p class="muted">点击小图可查看大图。</p>
        </div>
      </div>
      <div class="photo-grid compact-grid">
        ${project.declaration.photoDataUrls
          .map(
            (photo, index) => {
              const previewUrl = resolvePhotoUrl(photo);
              return `
              <article
                class="photo-card compact-photo-card reorderable-photo-card"
                data-photo-scope="declaration"
                data-photo-key="${escapeHtml(photo)}"
                data-photo-index="${index}"
              >
                <button
                  type="button"
                  class="preview-link preview-button"
                  data-preview-image="${previewUrl}"
                  data-preview-title="验收申请表照片 ${index + 1}"
                >
                  <img src="${previewUrl}" alt="验收申请表照片 ${index + 1}" />
                </button>
                <button type="button" class="photo-delete-button" data-declaration-photo-delete="${index}" aria-label="删除图片">×</button>
              </article>
            `;
            },
          )
          .join("")}
      </div>
    </div>
  `;
}

export function renderProjectDeclarationPage(
  project: ProjectSummary,
  _pendingCount: number,
  route?: Pick<RouteState, "source">,
): string {
  const currentMode = project.declaration.entryMode;
  const pdfLocked = currentMode === "photo" || currentMode === "skip";
  const photoLocked = currentMode === "pdf" || currentMode === "skip";
  const skipLocked = currentMode === "pdf" || currentMode === "photo";
  const fromBasicInfo = route?.source === "basic-info";
  const topBackLabel = fromBasicInfo ? "返回项目基本信息" : "返回项目";
  const bottomActionLabel = fromBasicInfo ? "返回项目基本信息" : "进入项目基本信息";
  const pagePath = fromBasicInfo
    ? [
        { label: project.name, href: `#/projects/home?id=${project.id}` },
        { label: "项目基本信息", href: `#/projects/basic-info?id=${project.id}` },
        { label: "验收申请表辅助入口" },
      ]
    : [{ label: project.name, href: `#/projects/home?id=${project.id}` }, { label: "消防验收申请表" }];
  const summaries = buildDeclarationSummaries(project);

  return `
    <section class="screen">
      <header class="topbar">
        <button class="ghost-button" data-action="back-to-home">${topBackLabel}</button>
        <h1>消防验收申请表入口页</h1>
      </header>
      ${renderPagePath(pagePath)}
      <div class="panel stack">
        <h2>${escapeHtml(project.name)}</h2>
        <p class="muted stage-note">这个页面现在只作为验收申请表附件留存入口使用，不再作为项目基本信息的必经前置步骤。</p>
        <p class="mini-meta stage-note">如果你现在想直接干活，可以跳过这里，直接进入项目基本信息页手动填写。</p>
        <div class="inline-pills">
          <span class="state-pill">本机自动保存</span>
          <span class="state-pill">
            ${
              currentMode === "pdf"
                ? "当前方式：PDF 上传"
                : currentMode === "photo"
                  ? "当前方式：拍照上传"
                  : currentMode === "skip"
                    ? "当前方式：稍后上传"
                    : "当前还未选择上传方式"
            }
          </span>
        </div>
      </div>
      <div class="panel stack">
        <div class="inline-head">
          <div>
            <p class="eyebrow">当前页进度</p>
            <p class="muted">先看附件、辅助预填和主线衔接状态，再决定要不要继续停留在这一页。</p>
          </div>
        </div>
        <div class="stack">
          ${summaries
            .map(
              (summary) => `
                <div class="readiness-row">
                  <div class="stack">
                    <div class="inline-pills">
                      <strong>${summary.label}</strong>
                      <span class="state-pill ${summary.warning ? "is-warning" : ""}">${summary.stateLabel}</span>
                    </div>
                    <p class="mini-meta">${escapeHtml(summary.detail)}</p>
                  </div>
                </div>
              `,
            )
            .join("")}
        </div>
      </div>
      <div class="panel stack">
        <p class="eyebrow">上传验收申请表</p>
        <div class="stack single-action-stack">
          ${renderModeButton("PDF 上传", "declaration-pdf-input", pdfLocked, currentMode === "pdf", ".pdf,application/pdf")}
          ${renderModeButton("拍照上传", "declaration-photo-input", photoLocked, currentMode === "photo", "image/*", true)}
        </div>
        <p class="mini-meta stage-note">如果 PDF 是从微信里下载的，H5 网页版不能直接指定打开微信下载目录。建议先把文件存到手机“文件/下载”里，再点“PDF 上传”去系统文件里选择。</p>
        <button class="ghost-button ${skipLocked ? "is-disabled" : ""} ${currentMode === "skip" ? "is-selected" : ""}" type="button" data-action="skip-declaration-upload" ${skipLocked ? "disabled" : ""}>稍后上传</button>
        ${
          currentMode === "skip"
            ? `<button class="ghost-button small-button" type="button" data-action="clear-declaration-skip">取消稍后上传</button>`
            : ""
        }
      </div>
      ${renderPdfPreview(project)}
      ${renderPhotoPreview(project)}
      <div class="panel stack">
        <p class="muted">后面如果需要补传验收申请表附件，再回到这里即可；当前版本以手动填写项目基本信息和项目概况为准。</p>
        <button class="nav-button" type="button" data-action="go-basic-info">${bottomActionLabel}</button>
      </div>
    </section>
  `;
}
